const clampProb = (p) => Math.min(Math.max(p, 1e-15), 1 - 1e-15);

/** Mean log loss over { p, y } pairs; p is clamped so a perfect call never returns Infinity. */
export function logLoss(pairs) {
  const losses = pairs.map(({ p, y }) => {
    const pc = clampProb(p);
    return -(y * Math.log(pc) + (1 - y) * Math.log(1 - pc));
  });
  return losses.reduce((sum, x) => sum + x, 0) / losses.length;
}

/** Mean Brier score over { p, y } pairs. */
export function brier(pairs) {
  const scores = pairs.map(({ p, y }) => (p - y) ** 2);
  return scores.reduce((sum, x) => sum + x, 0) / scores.length;
}

/** Reliability curve: mean predicted vs. mean actual per probability bin, empty bins skipped. */
export function calibration(pairs, bins = 10) {
  const out = [];
  for (let i = 0; i < bins; i++) {
    const lo = i / bins;
    const hi = (i + 1) / bins;
    const last = i === bins - 1;
    const inBin = pairs.filter(({ p }) => p >= lo && (p < hi || (last && p === 1)));
    if (inBin.length === 0) continue;
    out.push({
      lo,
      hi,
      n: inBin.length,
      meanP: inBin.reduce((sum, x) => sum + x.p, 0) / inBin.length,
      meanY: inBin.reduce((sum, x) => sum + x.y, 0) / inBin.length,
    });
  }
  return out;
}

/** Return on investment; 0 when nothing was staked. */
export function roi({ staked, returned }) {
  return staked === 0 ? 0 : (returned - staked) / staked;
}

/** Fraction of decided bets that won (void excluded); 0 when none are decided. */
export function strikeRate(bets) {
  const decided = bets.filter((b) => b.result === 'won' || b.result === 'lost');
  return decided.length === 0 ? 0 : decided.filter((b) => b.result === 'won').length / decided.length;
}

/** Largest peak-to-trough decline in an equity curve, as a fraction of the peak. */
export function maxDrawdown(equity) {
  if (equity.length <= 1) return 0;
  let peak = equity[0];
  let worst = 0;
  for (const x of equity) {
    if (x > peak) peak = x;
    const dd = peak > 0 ? (peak - x) / peak : 0;
    if (dd > worst) worst = dd;
  }
  return worst;
}

/** Closing-line value for one bet; null if either price is missing or not a usable price. */
export function clv({ priceTaken, closingPrice }) {
  if (!Number.isFinite(priceTaken) || !Number.isFinite(closingPrice)) return null;
  if (priceTaken <= 1 || closingPrice <= 1) return null;
  return priceTaken / closingPrice - 1;
}

/**
 * CLV against a DEVIGGED close: what a dollar at the taken price is worth once
 * the market has finished making its mind up (engine review #6).
 *
 * `clv` above divides one quoted price by another, which measures two things at
 * once — where the market moved, and how much margin the quoting book happened
 * to be holding at each end. A book that opens fat and reverts to the field
 * prints CLV for nobody's skill. This form multiplies the price taken by the
 * closing FAIR PROBABILITY, so what comes back is expected value at the close:
 * +0.018 is "a dollar here is worth $1.018 against what the market settled on".
 *
 * The caller decides whose fair probability that is, and that decision is the
 * whole of the honesty — see `closingFairFor` in stores/settlement.js: the
 * exchange net of commission where it priced the side, else the board without
 * the bet's own book, else nothing at all.
 */
export function clvFair({ priceTaken, pClosingFair }) {
  if (!Number.isFinite(priceTaken) || !Number.isFinite(pClosingFair)) return null;
  if (priceTaken <= 1 || pClosingFair <= 0 || pClosingFair >= 1) return null;
  return pClosingFair * priceTaken - 1;
}

/** 1.96: the two-sided 95% normal quantile, which is what every interval here means. */
export const Z95 = 1.96;

/**
 * A 95% interval on the MEAN of a sample, from its three counters — how many,
 * their sum, and the sum of their squares.
 *
 * Counters rather than the values themselves because that is what survives the
 * fold: `insights-rollup.js` keeps a phone's record as a handful of running
 * totals per strategy, and an interval that needed the legs back would be an
 * interval only the last two days could have.
 *
 * Normal approximation, deliberately. Per-leg CLV is a bounded, mildly skewed
 * thing and the interval is being printed as "give or take", not published; at
 * the sample sizes that reach this (`CLV_MIN_LEGS` up, hundreds at the top) the
 * t correction is under 2% of the half-width and a bootstrap would need the
 * sample it does not have. Null under two values, where there is no spread to
 * measure — one leg is not an interval, it is a number.
 */
export function meanInterval({ n, sum, sumSq } = {}, z = Z95) {
  if (!Number.isFinite(n) || n < 2) return null;
  if (!Number.isFinite(sum) || !Number.isFinite(sumSq)) return null;
  const mean = sum / n;
  // Clamped at zero: the computational form of the variance can come out a
  // whisker negative on a sample whose values are all but identical, and a NaN
  // half-width would print as "±—" on a screen that is trying to say "sure".
  const variance = Math.max(0, (sumSq - (sum * sum) / n) / (n - 1));
  const half = z * Math.sqrt(variance / n);
  return { n, mean, half, lo: mean - half, hi: mean + half };
}

/**
 * mulberry32, so a resample is a fact about the data rather than about the
 * afternoon it was run on. Same generator the demo fixtures use; it lives here
 * too rather than being imported from them, because the engine must not depend
 * on a fixture.
 */
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** How many resamples a bootstrap interval is built from. */
export const BOOTSTRAP_RESAMPLES = 1000;

/**
 * A bootstrap 95% interval on ROI, from the per-bet stakes and returns.
 *
 * ROI IS A RATIO OF SUMS, not a mean, so `meanInterval` is the wrong shape for
 * it: with varying stakes the denominator is itself a random quantity, and the
 * standard error of a ratio has no closed form worth trusting on 300 bets whose
 * returns are mostly zero and occasionally forty times the stake. Resampling
 * the BETS — with replacement, `resamples` times, recomputing the whole ratio
 * each time — gets the skew right by construction.
 *
 * The percentile interval (2.5th and 97.5th of the resampled ROIs) rather than
 * the normal one, for the same reason: the distribution of a multi strategy's
 * ROI is not symmetric and pretending it is would put the lower bound in the
 * wrong place, which is the bound a punter reads.
 *
 * Seeded, so the same run prints the same interval forever. Null under two
 * bets, or where nothing was staked.
 */
export function bootstrapRoi(bets, { resamples = BOOTSTRAP_RESAMPLES, seed = 0 } = {}) {
  const list = (bets ?? []).filter((b) => Number.isFinite(b?.stake) && Number.isFinite(b?.returned));
  if (list.length < 2) return null;
  const staked = list.reduce((a, b) => a + b.stake, 0);
  if (!(staked > 0)) return null;

  const rand = mulberry32(seed);
  const n = list.length;
  const draws = [];
  for (let i = 0; i < resamples; i += 1) {
    let s = 0;
    let r = 0;
    for (let j = 0; j < n; j += 1) {
      const bet = list[Math.floor(rand() * n)];
      s += bet.stake;
      r += bet.returned;
    }
    if (s > 0) draws.push((r - s) / s);
  }
  if (draws.length < 2) return null;
  draws.sort((a, b) => a - b);
  const at = (q) => draws[Math.min(draws.length - 1, Math.max(0, Math.round(q * (draws.length - 1))))];
  const returned = list.reduce((a, b) => a + b.returned, 0);
  return { n, roi: (returned - staked) / staked, lo: at(0.025), hi: at(0.975), resamples: draws.length };
}

/** CLV coverage and average/median over the legs that have a closing price. */
export function clvStats(legs) {
  const values = legs
    .map((l) => clv(l))
    .filter((v) => v !== null)
    .sort((a, b) => a - b);
  if (values.length === 0) {
    return { n: legs.length, covered: 0, avgClv: null, medianClv: null };
  }
  const mid = Math.floor(values.length / 2);
  const medianClv = values.length % 2 === 0 ? (values[mid - 1] + values[mid]) / 2 : values[mid];
  return {
    n: legs.length,
    covered: values.length,
    avgClv: values.reduce((sum, x) => sum + x, 0) / values.length,
    medianClv,
  };
}

/**
 * What a pile of SETTLED legs is worth against the close: `{ n, mean,
 * interval }` over the ones E4 could actually measure.
 *
 * A CLOSING PRICE IS NOT THE SAME THING AS A MEASURABLE CLOSE (engine review
 * #6). Every settled leg may carry the price its own book settled at; only the
 * ones with a `clvSource` were read against the exchange, or against the board
 * without the book the bet was struck at, and only those are evidence about
 * whether the pad beat the market. A leg settled before that rule and never
 * re-derived has the price and not the answer, and it counts for nothing here
 * rather than being re-measured against its own shop's vig.
 *
 * `clvStats` above is the OLD arithmetic and is kept for the backtest, which
 * scores a season of stored `priceTaken`/`closingPrice` pairs off one book by
 * design. Anything reading a punter's own settled legs wants this one.
 */
export function measuredClv(legs) {
  let n = 0;
  let sum = 0;
  let sumSq = 0;
  for (const leg of legs ?? []) {
    if (!leg?.clvSource || !Number.isFinite(leg?.clv)) continue;
    n += 1;
    sum += leg.clv;
    sumSq += leg.clv * leg.clv;
  }
  return { n, mean: n ? sum / n : null, interval: meanInterval({ n, sum, sumSq }) };
}

/** Group items by keyFn(item) and reduce each group. */
export function breakdown(items, keyFn, reducer) {
  const groups = {};
  for (const item of items) {
    const key = keyFn(item);
    (groups[key] ??= []).push(item);
  }
  const out = {};
  for (const key of Object.keys(groups)) {
    out[key] = reducer(groups[key]);
  }
  return out;
}
